import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const InvestmentCalculator = ({ isOpen, onClose }) => {
  const [propertyPrice, setPropertyPrice] = useState('');
  const [downPayment, setDownPayment] = useState('20');
  const [interestRate, setInterestRate] = useState('8');
  const [loanTerm, setLoanTerm] = useState('20');
  const [results, setResults] = useState(null);

  const loanTermOptions = [
    { value: '10', label: '10 ans' },
    { value: '15', label: '15 ans' },
    { value: '20', label: '20 ans' },
    { value: '25', label: '25 ans' },
    { value: '30', label: '30 ans' }
  ];

  const calculateMortgage = () => {
    const price = parseFloat(propertyPrice);
    const down = parseFloat(downPayment);
    const rate = parseFloat(interestRate) / 100 / 12;
    const term = parseFloat(loanTerm) * 12;

    if (!price || !down || !rate || !term) return;

    const downPaymentAmount = (price * down) / 100;
    const loanAmount = price - downPaymentAmount;
    const monthlyPayment = (loanAmount * rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
    const totalPayment = monthlyPayment * term;
    const totalInterest = totalPayment - loanAmount;

    setResults({
      downPaymentAmount,
      loanAmount,
      monthlyPayment,
      totalPayment,
      totalInterest,
      roi: ((price * 0.05 * parseFloat(loanTerm)) / price) * 100
    });
  };

  useEffect(() => {
    if (propertyPrice && downPayment && interestRate && loanTerm) {
      calculateMortgage();
    }
  }, [propertyPrice, downPayment, interestRate, loanTerm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-white">
              Calculateur d'investissement
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Estimez vos paiements hypothécaires et ROI
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
            aria-label="Close calculator"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Paramètres du prêt
              </h3>

              <Input
                label="Prix de la propriété (CDF)"
                type="number"
                placeholder="Ex: 50000000"
                value={propertyPrice}
                onChange={(e) => setPropertyPrice(e?.target?.value)}
                required
              />

              <Input
                label="Acompte (%)"
                type="number"
                placeholder="Ex: 20"
                value={downPayment}
                onChange={(e) => setDownPayment(e?.target?.value)}
                min="0"
                max="100"
              />

              <Input
                label="Taux d'intérêt annuel (%)"
                type="number"
                placeholder="Ex: 8"
                value={interestRate}
                onChange={(e) => setInterestRate(e?.target?.value)}
                min="0"
                max="30"
              />

              <Select
                label="Durée du prêt"
                options={loanTermOptions}
                value={loanTerm}
                onChange={setLoanTerm}
              />

              <Button
                variant="default"
                fullWidth
                iconName="Calculator"
                iconPosition="left"
                onClick={calculateMortgage}
              >
                Calculer
              </Button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Résultats de l'analyse
              </h3>

              {results ? (
                <div className="space-y-3">
                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                    <p className="text-sm text-slate-400 mb-1">Acompte requis</p>
                    <p className="text-2xl font-bold text-blue-400">
                      {results?.downPaymentAmount?.toLocaleString('fr-FR')} CDF
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      ${(results?.downPaymentAmount / 2500)?.toLocaleString('en-US')} USD
                    </p>
                  </div>

                  <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20">
                    <p className="text-sm text-slate-400 mb-1">Montant du prêt</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {results?.loanAmount?.toLocaleString('fr-FR')} CDF
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      ${(results?.loanAmount / 2500)?.toLocaleString('en-US')} USD
                    </p>
                  </div>

                  <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
                    <p className="text-sm text-slate-400 mb-1">Paiement mensuel</p>
                    <p className="text-2xl font-bold text-green-400">
                      {results?.monthlyPayment?.toLocaleString('fr-FR')} CDF
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      ${(results?.monthlyPayment / 2500)?.toLocaleString('en-US')} USD
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Paiement total</p>
                      <p className="text-sm font-semibold text-white">
                        {results?.totalPayment?.toLocaleString('fr-FR')} CDF
                      </p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Intérêts totaux</p>
                      <p className="text-sm font-semibold text-white">
                        {results?.totalInterest?.toLocaleString('fr-FR')} CDF
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={20} />
                      <p className="text-sm font-medium">ROI estimé</p>
                    </div>
                    <p className="text-3xl font-bold">{results?.roi?.toFixed(2)}%</p>
                    <p className="text-xs opacity-90 mt-1">
                      Basé sur une appréciation annuelle de 5%
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4">
                    <Icon name="Calculator" size={32} className="text-slate-400" />
                  </div>
                  <p className="text-slate-400">
                    Entrez les détails de votre prêt pour voir les résultats
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-white/10">
          <div className="flex items-start gap-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
            <Icon name="AlertCircle" size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white mb-1">
                Estimation indicative
              </p>
              <p className="text-xs text-slate-400">
                Ces calculs sont fournis à titre indicatif uniquement. Consultez un conseiller financier pour des informations précises adaptées à votre situation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;
